"use client";

import React, { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  Info,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  Loader2,
} from "lucide-react";
import { useLanguage } from "./LanguageContext";
import {
  getMyNotifications,
  markNotificationRead,
  removeNotification as apiRemoveNotification,
} from "@/shared/api/notifications";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const typeConfig = {
  success: { icon: CheckCircle2, color: "text-cyan-400", bg: "bg-cyan-500/10" },
  warning: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10" },
  info: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10" },
  deposit: { icon: ArrowDownToLine, color: "text-blue-400", bg: "bg-blue-500/10" },
  withdrawal: { icon: ArrowUpFromLine, color: "text-cyan-400", bg: "bg-cyan-500/10" },
};

function mapFromApi(n) {
  return {
    id: n.id,
    title: n.title,
    message: n.message,
    type: n.type,
    read: n.isRead,
    time: new Date(n.createdAt),
  };
}

export default function NotificationDropdown({ accentColor = "cyan", className }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMyNotifications();
      setNotifications(list.map(mapFromApi));
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const unreadNotifications = notifications.filter((n) => !n.read);
  const unreadCount = unreadNotifications.length;

  const markAsRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
    } catch (_) {}
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    try {
      await Promise.all(unreadIds.map((id) => markNotificationRead(id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
    } catch (_) {}
  };

  const removeNotification = async (id) => {
    try {
      const ok = await apiRemoveNotification(id);
      if (ok) {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        queryClient.invalidateQueries({ queryKey: ["myNotifications"] });
      }
    } catch (_) {}
  };

  const accentColors = {
    cyan: "bg-cyan-400",
    blue: "bg-blue-400",
    violet: "bg-violet-400",
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative bg-[#0f172a] border border-cyan-500/20 text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400 hover:shadow-[0_0_12px_rgba(0,255,255,0.4)] transition-all duration-200 ${className || ""}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              className={`absolute top-1 right-1 w-2.5 h-2.5 ${accentColors[accentColor] || accentColors.cyan} rounded-full animate-pulse shadow-[0_0_8px_rgba(0,255,255,0.8)]`}
            />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 p-0 bg-gradient-to-br from-[#0f172a] via-[#0b1220] to-[#0f172a] border border-cyan-500/20 text-gray-200 shadow-[0_0_30px_rgba(0,255,255,0.15)] backdrop-blur-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-cyan-500/10">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm text-gray-200">{t("notifications")}</h3>
            {unreadCount > 0 && (
              <span className="text-xs bg-cyan-500/15 text-cyan-400 px-2 py-0.5 rounded-full">
                {unreadCount} {t("newNotifications") || "New"}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-cyan-400 hover:text-cyan-300 font-medium transition"
            >
              {t("markAllRead")}
            </button>
          )}
        </div>

        {/* Body */}
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 flex justify-center items-center gap-2 text-gray-400 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />
              {t("loading")}
            </div>
          ) : unreadNotifications.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">
              {t("noUnreadNotifications") || "No unread notifications"}
            </div>
          ) : (
            unreadNotifications.map((notification) => {
              const config = typeConfig[notification.type?.toLowerCase()] || typeConfig.info;
              const Icon = config.icon;

              return (
                <div
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  className={`flex items-start gap-3 p-3 cursor-pointer border-b border-cyan-500/5 last:border-b-0 hover:bg-cyan-500/10 transition-all duration-200 ${
                    !notification.read ? "bg-cyan-500/5" : ""
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${!notification.read ? "text-white" : "text-gray-400"}`}>
                        {notification.title}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        className="text-gray-500 hover:text-cyan-400 p-0.5 transition"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notification.message}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{format(notification.time, "h:mm a")}</p>
                  </div>

                  {!notification.read && (
                    <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0 mt-2 shadow-[0_0_6px_rgba(0,255,255,0.8)]" />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-2 border-t border-cyan-500/10 bg-[#0b1220]">
          <button
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="w-full text-center text-xs text-gray-400 hover:text-cyan-400 py-1.5 font-medium transition"
          >
            {t("viewAllNotifications")}
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
